import { FC, useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Box, Grid, Stack, Typography } from '@mui/material';
import isEmpty from 'lodash/isEmpty';

import { useRouter } from '@cvt/routing';

import HeartPulse from '@modules/Tributes/components/HeartPulse';
import { useTribute } from '@modules/Tributes/hooks/useTribute';
import { CreateTributeCard } from '@modules/Tributes/components/CreateTributeCard';
import { useMe } from '@modules/Auth/hooks/useMe';

import { PreviewStep } from '../Wizard/PreviewStep';
import { TributeFormData } from '../CreateTribute/CreateTribute';

interface Props {
  tributeId?: number | string;
}

export const EditTribute: FC<Props> = (props) => {
  const { tributeId } = props;
  const router = useRouter();

  const { user } = useMe();
  const { tribute } = useTribute({ id: tributeId || '' });
  const methods = useForm<TributeFormData>({
    defaultValues: {
      petName: '',
      petType: '',
      sex: '',
      petDescription: '',
      petDateOfBirth: null,
      petDateOfPassing: null,
      favouriteMemory: '',
      personality: '',
      specialHabits: '',
      mediaFiles: [],
      mainPhoto: null,
    },
  });

  const onPreviewStepComplete = useCallback(() => {
    router.tributes.myTributes.go();
  }, [router]);

  const canCreateTributeCard = useMemo(() => {
    if (user?.id === tribute?.author && isEmpty(tribute?.parcel) && tribute?.status === 'published') {
      return true;
    }
  }, [user, tribute]);

  if (!tribute) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          backgroundImage: 'linear-gradient(to right bottom, rgb(254, 247, 246), rgb(249, 249, 250))',
        }}
        paddingX={{ xs: 2, sm: 4, md: 6, lg: 8, xl: 10 }}
        pb={10}
      >
        <Grid container maxWidth="sm" width="100%" height="100%" mx="auto">
          <Grid size={12}>
            <Stack justifyContent="space-between" width="100%" mt={6}>
              <Stack alignItems="center" gap={1}>
                <HeartPulse color="primary.main" size={24} />
                <Typography variant="h4" component="h1" fontWeight={700} fontSize={30} textAlign="center">
                  Edit LGCY for {tribute.pet.name}
                </Typography>
                <Typography variant="body1" fontSize={18} color="textSecondary" textAlign="center">
                  Update the memorial timeline that captures the love and joy your pet brought to your life.
                </Typography>
              </Stack>

              <PreviewStep
                onComplete={onPreviewStepComplete}
                initialData={tribute}
                isEditing={true}
              />

              {canCreateTributeCard && (
                <Box px={{ xs: 0, sm: 2 }}>
                  <CreateTributeCard
                    tribute={tribute}
                  />
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};
